class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          
          // --- Custom Logic: Age Gate Filtering & Backfill ---
          const isVerified = sessionStorage.getItem('after_dark_verified') === 'true';
          
          // 1. Determine how many products we actually WANT to show.
          // We grab this from the original data-url on the component, which reflects the section settings.
          // Default to 4 if not found.
          const limitMatch = this.dataset.url.match(/limit=(\d+)/);
          const desiredLimit = limitMatch ? parseInt(limitMatch[1], 10) : 4;

          const items = recommendations.querySelectorAll('.resource-list__item');
          let validCount = 0;
          let hasVisibleItems = false;

          items.forEach((item) => {
            const tags = (item.dataset.productTags || '').split(',');
            const isNSFW = tags.includes('nsfw');
            
            let shouldRemove = false;

            // Filter Condition: Unverified user + NSFW tag
            if (!isVerified && isNSFW) {
              shouldRemove = true;
            } else {
              // It's a valid product, but do we have space for it?
              // We fetched 10 (buffer), but we only want to show 'desiredLimit' (e.g. 4).
              if (validCount >= desiredLimit) {
                 shouldRemove = true; // We have enough products, remove this extra one.
              } else {
                 validCount++;
                 hasVisibleItems = true;
              }
            }

            if (shouldRemove) {
              const parent = item.parentElement;
              item.remove();
              
              // If the parent wrapper becomes empty (common in some carousel structures), remove it too.
              if (parent && parent.children.length === 0 && parent.textContent.trim() === '') {
                parent.remove();
              }
            }
          });

          if (items.length > 0 && !hasVisibleItems) {
             this.#handleError(new Error('No allowed recommendations available after filtering'));
             return;
          }
          // --- End Custom Logic ---

          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    // We force the limit to 10 here to create a buffer. 
    // This allows us to filter out NSFW items and still have enough products to fill the row.
    const urlWithBuffer = this.dataset.url.replace(/limit=\d+/, 'limit=10');
    
    const url = `${urlWithBuffer}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}